<!-- eslint-disable vue/no-v-html -->
<template>
  <component
    :is="url ? 'a' : 'div'"
    :class="['site-result',
             { 'site-result--with-flash-coming-soon': isComingSoon },
             { 'site-result--with-flash': !isComingSoon && isNew }]"
    :href="url">
    <span
      v-if="isComingSoon"
      class="flash flash--coming-soon">
      Coming soon
    </span>
    <span
      v-else-if="isNew"
      class="flash flash--new">
      New
    </span>

    <div class="site-result__details-container">
      <div class="site-result__details">
        <span class="site-result__type">
          {{ displayType }}
        </span>

        <ul
          v-if="hasAges || hasCategories || hasFormats"
          class="site-result__tags">
          <template v-if="hasCategories">
            <li v-for="(category, index) in categories"
                :key="index"
                class="site-result__tag">
              <category-tag :value="category"></category-tag>
            </li>
          </template>

          <template v-if="hasAges">
            <li v-for="(age, index) in ages"
                :key="index"
                class="site-result__tag">
              <age-tag :value="age"></age-tag>
            </li>
          </template>

          <template v-if="hasFormats">
            <li v-for="(format, index) in formats"
                :key="index"
                class="site-result__tag">
              <format-tag :value="format"></format-tag>
            </li>
          </template>
        </ul>

        <div class="site-result__formats">
          <span
            v-if="isPrint"
            class="site-result__format">
            <span class="round-icon">
              <font-awesome-icon icon="book"
                                 title="Print">
              </font-awesome-icon>
            </span>
            Print
          </span>

          <span
            v-else-if="isDigital"
            class="site-result__format">
            <span class="round-icon">
              <font-awesome-icon icon="download"
                                 title="Digital">
              </font-awesome-icon>
            </span>
            Digital
          </span>
        </div>
      </div>
    </div>

    <div :class="['site-result__content',
                  { 'site-result__content--with-flash-coming-soon': isComingSoon },
                  { 'site-result__content--with-flash': !isComingSoon && isNew }]">
      <h3 class="site-result__name">
        {{ name }}
      </h3>

      <div
        class="site-result__description"
        v-html="searchDescription">
      </div>

      <p v-if="userMessage"
         class="site-result__user-message">
        {{ userMessage }}
      </p>

      <div class="site-result__read-more">
        Read more
        <font-awesome-icon
          icon="chevron-right"
          role="presentation"></font-awesome-icon>
      </div>
    </div>
  </component>
</template>

<script>
export default {
  name: "SiteSearchResult",
  props: {
    url: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: "",
    },
    searchDescription: {
      type: String,
      default: null,
    },
    hasWebPage: {
      type: Boolean,
      default: false,
    },
    isNew: {
      type: Boolean,
      default: true,
    },
    isComingSoon: {
      type: Boolean,
      default: false,
    },
    ages: {
      type: Object,
      default: () => {},
    },
    categories: {
      type: Object,
      default: () => {},
    },
    formats: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: "content",
    },
  },
  computed: {
    displayType () {
      let result = "Content";

      const okToGo = ["Product", "Publication", "Course", "Conference", "Workshop", "Subscription", "Webinar Package"];
      if ( okToGo.includes( this.type ) ) {
        result = this.type;
      } else {
        switch ( this.type ) {
          case "newsItem": {
            result = "Blog article";
            break;
          }
          case "newsArea": {
            result = "Blog";
            break;
          }
          case "videoCollection": {
            result = "Video collection";
            break;
          }
          case "videoClip": {
            result = "Video clip";
            break;
          }
          case "projectItem": {
            result = "Project";
            break;
          }
          case "magazineIssueItem": {
            result = "Magazine issue";
            break;
          }
          case "magazineArticleItem": {
            result = "Magazine article";
            break;
          }
          case "magazineItem": {
            result = "Magazine";
            break;
          }
          case "competitionItem": {
            result = "Competition";
            break;
          }
          default: {
            result = `[${this.type}]`;
          }
        }
      }

      return result;
    },
    hasAges () {
      let result = false;

      if ( this.ages && Object.keys( this.ages ).length > 0 ) {
        result = true;
      }

      return result;
    },
    hasCategories () {
      let result = false;

      if ( this.categories && Object.keys( this.categories ).length > 0 ) {
        result = true;
      }

      return result;
    },
    hasFormats () {
      let result = false;

      if ( this.formats && Object.keys( this.formats ).length > 0 ) {
        result = true;
      }

      return result;
    },
    isDigital () {
      let result = false;

      if ( this.formats ) {
        result = Object.values( this.formats )?.some( x => x === "Download" );
      }

      return result;
    },
    isPrint () {
      let result = false;

      if ( this.formats ) {
        result = Object.values( this.formats )?.some( x => x === "Print" );
      }

      return result;
    },
    userMessage () {
      let result = null;

      if ( this.$attrs.userMessage ) {
        result = this.$attrs.userMessage;
      }

      return result;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.site-result {
  @padding-y: 15px;
  position: relative;
  width: 100%;
  border-bottom: 1px solid @color-accent-5-light;
  display: flex;
  justify-content: space-between;
  padding: @padding-y 0;
  text-decoration: none;

  &:hover {
    background: fade( @color-accent-5-lighter, 20 );
  }

  @media ( max-width: 767px ) {
    flex-flow: column;

    .flash {
      margin-bottom: 1rem;
      position: relative;
      font-size: 1rem;
      width: 100%;
      height: unset;
      padding: 0.5rem 0;
      text-align: center;
      clip-path: unset;
    }
  }

  &--with-flash {
    @additional-height: @padding-y;
    min-height: calc( @additional-height + 120px );

    &-coming-soon {
      min-height: calc( @additional-height + 160px );
    }
  }

  &__details-container {
    @media ( min-width: 767px ) {
      flex-basis: calc( 33.33% );
      flex-grow: 0;
      flex-shrink: 0;
    }
    @media ( max-width: 767px ) {
      display: flex;
      justify-content: space-between;
      flex-flow: wrap-reverse;
    }
  }

  &__details {
    @media ( max-width: 767px ) {
      max-width: 300px;
    }
  }

  &__content {
    @media ( max-width: 767px ) {
      padding-top: 1rem;
    }
    @media ( min-width:  767px ) {
      padding-left: 1rem;
      flex-basis: calc( 66.66% );
      flex-grow: 0;
      flex-shrink: 0
    }

    &--with-flash {
      @additional-padding: 15px;
      padding-right: calc( @additional-padding + 120px );

      @media ( max-width: 767px ) {
        padding-right: 0;
      }

      &-coming-soon {
        padding-right: calc( @additional-padding + 170px );

        @media ( max-width: 767px ) {
          padding-right: 0;
        }
      }
    }
  }

  &__type {
    display: block;
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 8px;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    margin: -0.75rem 0 0.75rem -0.75rem;
    list-style: none;
  }

  &__tag {
    margin: 0.75rem 0 0 0.75rem;
  }

  &__format {
    display: flex;
    align-items: center;

    .round-icon {
      margin-right: 10px;
    }
  }

  &__name {
    font-size: 1rem;
    font-weight: 500;
  }

  &__name,
  &__description {
    margin-bottom: 10px;
  }

  &__read-more {
    font-weight: 600;
  }

  &__user-message {
    color: #000;
    font-weight: 500;
  }
}
</style>
